import React, { createContext, useContext, useEffect, useReducer } from 'react';

import {
  ACTION_LOG_ERROR,
  connect,
  getSessionToken,
  getStoreInfo,
  iAmReady
} from '@tiendanube/nexo';
import { ECountry, ECurrency, ELocale } from 'App/i18n/i18n.types';
import { useTranslation } from 'react-i18next';

import { fetchConfigs } from 'commons/api/store-configs';
import { token } from 'commons/utils';
import queryClient from 'commons/utils/query';

import nexo from 'nexoClient';

import {
  initAmplitude,
  setAmplitudeGlobalEventProperties,
  setAmplitudeUserId,
  setAmplitudeUserProperties
} from '../amplitude';
import { setDatadogUser } from '../datadog/settings';
import { Define } from '../nexo/define';
import { fetchUnpaidCycles } from '../query/billing';
import { fetch } from '../query/store';
import { ProviderProps } from '../types/provider';
import { StoreResponseDto } from '../types/store-response-dto';

import { initialState, storeReducer, StoresState } from './reducer/stores';

import '../../../App/i18n';

const decodeJwtRoles = (jwt: string): string[] => {
  try {
    const parts = jwt.split('.');
    if (parts.length < 2) return [];
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    return Array.isArray(payload?.data?.roles)
      ? (payload.data.roles as string[])
      : [];
  } catch {
    return [];
  }
};

const StoreContext = createContext<StoresState | undefined>(undefined);

const useStoreContext = (): StoresState => {
  const context = useContext(StoreContext);

  if (!context) {
    throw new Error('useStoreContext must be used within a StoreProvider');
  }

  return context;
};

initAmplitude();

const StoreProvider: React.FC<ProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(storeReducer, initialState);

  const { i18n } = useTranslation();

  const query = new URLSearchParams(window.location.search);

  const usingNexo = new Define();

  const getCountryByMerchant = (country: string) => {
    switch (country.toLowerCase()) {
      case 'argentina':
        return ECountry.ARGENTINA;
      case 'brasil':
        return ECountry.BRAZIL;
      case 'méxico':
      case 'mexico':
        return ECountry.MEXICO;
      default:
        return ECountry.BRAZIL;
    }
  };

  const getCurrencyByMerchant = (country: ECountry) => {
    switch (country) {
      case ECountry.ARGENTINA:
        return ECurrency.ARGENTINA;
      case ECountry.BRAZIL:
        return ECurrency.BRAZIL;
      case ECountry.MEXICO:
        return ECurrency.MEXICO;
    }
  };

  const getLanguageByMerchant = (country: ECountry) => {
    switch (country) {
      case ECountry.ARGENTINA:
        return ELocale.ARGENTINA;
      case ECountry.BRAZIL:
        return ELocale.BRAZIL;
      case ECountry.MEXICO:
        return ELocale.MEXICO;
    }
  };

  async function getUnpaidCycles(storesResponse: StoreResponseDto) {
    if (storesResponse && storesResponse?.billingType === 'postpaid') {
      return await fetchUnpaidCycles();
    }
    return undefined;
  }

  useEffect(() => {
    connect(nexo)
      .then(async () => {
        const storeInfo = await getStoreInfo(nexo);
        const lang = `${storeInfo?.language}-${storeInfo?.country}`;

        i18n.changeLanguage(lang);
        iAmReady(nexo);

        setAmplitudeUserId(storeInfo.id);
        setAmplitudeUserProperties({ country_store: storeInfo.country });
        setAmplitudeGlobalEventProperties({ country_store: storeInfo.country });
        usingNexo.nexo = true;

        const [storesResponse, configsResponse, sessionToken] =
          await Promise.all([
            fetch(),
            fetchConfigs(),
            getSessionToken(nexo).catch(() => '')
          ]);

        queryClient.setQueryData(['store-configs'], configsResponse);

        dispatch({
          type: 'SET_DEFINITIONS',
          payload: {
            ...storesResponse,
            loading: false,
            language: `${storeInfo?.language}-${storeInfo?.country}`,
            currency: getCurrencyByMerchant(storeInfo.country as ECountry),
            country: storeInfo?.country as ECountry,
            unpaidCycles: await getUnpaidCycles(storesResponse),
            roles: decodeJwtRoles(sessionToken)
          }
        });

        setDatadogUser({ id: storeInfo.id, name: storeInfo.name });

        iAmReady(nexo);
      })
      .catch(async () => {
        // eslint-disable-next-line no-console
        console.log('nexo nao responde');
        nexo.dispatch({ type: ACTION_LOG_ERROR });

        if (process.env.REACT_APP_CURRENT_ENVIRONMENT === 'staging') {
          const storeId =
            query.get('store_id') || localStorage.getItem('storeId');
          const jwt = await token.generate(storeId);

          setAmplitudeUserId(query.get('store_id'));

          localStorage.setItem('token', jwt || '');
          localStorage.setItem('auth', 'true');
          localStorage.setItem('storeId', storeId || '');

          const storesResponse = await fetch();
          const configsResponse = await fetchConfigs();

          queryClient.setQueryData(['store-configs'], configsResponse);

          const country = storesResponse?.address?.country
            ? getCountryByMerchant(storesResponse.address.country)
            : ECountry.BRAZIL;

          setAmplitudeUserProperties({ country_store: country });
          setAmplitudeGlobalEventProperties({ country_store: country });

          dispatch({
            type: 'SET_DEFINITIONS',
            payload: {
              loading: false,
              language: getLanguageByMerchant(country as ECountry),
              currency: getCurrencyByMerchant(country as ECountry),
              ...storesResponse,
              unpaidCycles: await getUnpaidCycles(storesResponse),
              country,
              roles: decodeJwtRoles(jwt || '')
            }
          });
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StoreContext.Provider value={state}>{children}</StoreContext.Provider>
  );
};

export { StoreContext, StoreProvider, useStoreContext };
